/**
 * Trunnion AI - System Health Card
 * Displays system health indicators.
 */

import { Activity, GitBranch, Play, Bot, DollarSign, BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { StatusDot } from '@/components/ui/StatusDot';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useDashboardOverview } from '@/hooks/queries/useDashboard';

export function SystemHealthCard() {
  const { data, isLoading } = useDashboardOverview();

  if (isLoading) {
    return (
      <Card>
        <LoadingSpinner size="sm" />
      </Card>
    );
  }

  const metrics = [
    {
      icon: GitBranch,
      label: 'Workflows',
      value: `${data?.activeWorkflows ?? 0} / ${data?.totalWorkflows ?? 0}`,
      sub: 'active',
    },
    {
      icon: Play,
      label: 'Executions',
      value: `${data?.activeExecutions ?? 0}`,
      sub: 'running',
    },
    {
      icon: Bot,
      label: 'Agents',
      value: `${data?.activeAgents ?? 0} / ${data?.totalAgents ?? 0}`,
      sub: 'active',
    },
    {
      icon: BarChart3,
      label: 'Success Rate',
      value: `${((data?.successRate ?? 0) * 100).toFixed(1)}%`,
      sub: 'all time',
    },
    {
      icon: DollarSign,
      label: 'Cost Today',
      value: `$${(data?.estimatedCostToday ?? 0).toFixed(2)}`,
      sub: `${(data?.tokenUsageToday ?? 0).toLocaleString()} tokens`,
    },
  ];

  return (
    <Card padding="none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <Activity className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
            System Health
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <StatusDot
              status={
                data?.systemHealth === 'healthy'
                  ? 'healthy'
                  : data?.systemHealth === 'degraded'
                    ? 'degraded'
                    : 'unhealthy'
              }
              pulse
            />
            <span className="text-xs text-trunnion-slate-400 capitalize">
              {data?.systemHealth ?? 'unknown'}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {metrics.map(({ icon: Icon, label, value, sub }) => (
            <div
              key={label}
              className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 p-3 text-center"
            >
              <Icon className="h-4 w-4 mx-auto text-trunnion-slate-400 mb-1.5" />
              <p className="text-lg font-bold text-white">{value}</p>
              <p className="text-xs text-trunnion-slate-400">{label}</p>
              <p className="text-[10px] text-trunnion-slate-500">{sub}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
